import React, { FC } from "react";
import { observer } from "mobx-react-lite";
import { Draggable } from "@hello-pangea/dnd";
import { Tab } from "@/store/navigation/types";
import { useAppStore } from "@/store";
import { routingModule } from "@/modules/routing";
import { MDSTab } from "@/mds/components/tabs/tab";
import { matchPath } from "react-router";
import { appRoutes } from "@/app/router/appRoutes";
import { parseRoutingPathname } from "@/modules/routing/parsePathname";
import {
  getSmartViewIdFromPath,
  hasSmartViewUnreadUpdate,
} from "@/smart-views/client/update-notifications";

interface DraggableTabProps {
  tab: Tab;
  index: number;
  isActive: boolean;
  canShowSplitPaneButton: boolean;
  onTabClick: (tabId: string) => void;
  onCloseTab: (tabId: string) => void;
  onSplitPaneWithTab: (tabId: string) => void;
}

export const DraggableTab: FC<DraggableTabProps> = observer(
  ({
    tab,
    index,
    isActive,
    canShowSplitPaneButton,
    onTabClick,
    onCloseTab,
    onSplitPaneWithTab,
  }) => {
    const { store } = useAppStore();
    const tabTitle = routingModule.getPathTitle(tab.path, store);
    const tabIcon = routingModule.getPathIcon(tab.path, store);
    const smartViewId = getSmartViewIdFromPath(tab.path);
    const hasUnreadUpdate = smartViewId ? hasSmartViewUnreadUpdate(smartViewId) : false;

    const noteIdMatch = matchPath(
      appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
      parseRoutingPathname(tab.path)
    );
    const isActivelyRecording = noteIdMatch?.params.noteId === store.audio.activeRecordingNoteId;

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onTabClick(tab.id);
    };

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation();
      onCloseTab(tab.id);
    };

    const handleSplitPaneWithTab = (e: React.MouseEvent) => {
      e.stopPropagation();
      onSplitPaneWithTab(tab.id);
    };

    return (
      <Draggable draggableId={tab.id} index={index} isDragDisabled={false}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            data-tab-id={tab.id}
          >
            <MDSTab
              iconKind={tabIcon}
              isActive={isActive}
              isActivelyRecording={isActivelyRecording}
              isDragging={snapshot.isDragging}
              hasUnreadUpdate={hasUnreadUpdate}
              onClick={handleClick}
              onClickSplit={canShowSplitPaneButton ? handleSplitPaneWithTab : undefined}
              onClickClose={handleClose}
            >
              {tabTitle}
            </MDSTab>
          </div>
        )}
      </Draggable>
    );
  }
);
